import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Grid,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  MenuBook,
  MonetizationOn,
  VerifiedUser,
  PlayArrow,
  Stop,
  Close,
} from "@mui/icons-material";
import DonationsTable from "../stream-management/components/memorial-page/components/chema/DonationsTable";
import apiService from "../../service/Api";

interface StreamResponse {
  id: number;
  title: string;
  date: string;
  booking: {
    id: number;
    title: string;
    deceased?: Array<{
      first_name: string;
      surname: string;
    }>;
  };
  go_fund_me_enabled: boolean;
  created_at: string;
}

interface StreamsApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: StreamResponse[];
}

interface Fund {
  date: string;
  fundName: string;
  status: string;
  totalRaised: string;
  action: string;
  bookingId?: number;
}

const Insights = () => {
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);

  const stats = [
    {
      title: "Bookings with CHEMA",
      value: "32/40",
      icon: <MenuBook sx={{ color: "#10b981" }} />,
      iconBg: "#ecfdf5",
    },
    {
      title: "Total CHEMA",
      value: "$3200",
      icon: <MonetizationOn sx={{ color: "#f59e0b" }} />,
      iconBg: "#fffbeb",
    },
    {
      title: "Pending Approvals",
      value: "12/16",
      icon: <VerifiedUser sx={{ color: "#3b82f6" }} />,
      iconBg: "#eff6ff",
    },
  ];

  const [rows, setRows] = useState<Fund[]>([]);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [statusIndex, setStatusIndex] = useState<number | null>(null);
  const [actionAnchor, setActionAnchor] = useState<HTMLElement | null>(null);
  const [actionIndex, setActionIndex] = useState<number | null>(null);
  const [donationsDialogOpen, setDonationsDialogOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState<Fund | null>(null);

  useEffect(() => {
    const fetchStreams = async () => {
      setLoading(true);
      try {
        // Fetch all streams with go_fund_me_enabled
        const response = await apiService.get<StreamsApiResponse>(
          "/api/streams/stream/",
          { go_fund_me_enabled: true }
        );

        const streams = response.data?.results || [];
        
        // Transform streams to funds format
        const fundsData: Fund[] = streams.map((stream: StreamResponse) => {
          const deceasedName = stream.booking?.deceased?.[0]
            ? `${stream.booking.deceased[0].first_name} ${stream.booking.deceased[0].surname}`
            : stream.title;

          // Format date (YY/MM/DD)
          const formatDate = (dateString: string) => {
            try {
              const date = new Date(dateString);
              const year = date.getFullYear().toString().slice(-2);
              const month = (date.getMonth() + 1).toString().padStart(2, "0");
              const day = date.getDate().toString().padStart(2, "0");
              return `${year}/${month}/${day}`;
            } catch {
              return dateString;
            }
          };

          return {
            date: formatDate(stream.date || stream.created_at),
            fundName: deceasedName,
            status: "Approved", // Default status, can be updated from API if available
            totalRaised: "$0", // Can be calculated from donations if needed
            action: "Start", // Default action
            bookingId: stream.booking?.id,
          };
        });

        setFunds(fundsData);
        setRows(fundsData);
      } catch (error) {
        console.error("Error fetching streams:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStreams();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f3f4f6", minHeight: "100vh" }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
        CHEMA INSIGHTS
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid size={{ xs: 12, md: 4 }} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                height: "100%",
              }}
            >
              <Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {stat.title}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stat.value}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: stat.iconBg,
                  p: 1.5,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {stat.icon}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Funds Listing Table */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            CHEMA Funds Listing
          </Typography>
          <Button
            variant="outlined"
            sx={{
              textTransform: "none",
              color: "text.primary",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            Generate Report
          </Button>
        </Box>

        <Table
            sx={{
                "& td, & th": {
                borderBottom: "none",
                },
            }}
        >
            <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Fund Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Total Raised</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Manage</TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {rows.map((fund, index) => (
                <TableRow key={index}>
                    {/* Date */}
                    <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#9ca3af",
                        }}
                        />
                        {fund.date}
                    </Box>
                    </TableCell>

                    {/* Fund Name */}
                    <TableCell 
                      sx={{ 
                        fontWeight: "bold",
                        cursor: fund.bookingId ? "pointer" : "default",
                        "&:hover": fund.bookingId ? {
                          textDecoration: "underline",
                          color: "primary.main",
                        } : {},
                      }}
                      onClick={() => {
                        if (fund.bookingId) {
                          setSelectedFund(fund);
                          setDonationsDialogOpen(true);
                        }
                      }}
                    >
                    {fund.fundName}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                    <Chip
                        label={fund.status}
                        size="small"
                        onClick={(e) => {
                          setStatusAnchor(e.currentTarget);
                          setStatusIndex(index);
                        }}
                        sx={{
                        cursor: "pointer",
                        bgcolor:
                            fund.status === "Approved"
                            ? "#dbeafe"
                            : "#f3f4f6",
                        color:
                            fund.status === "Approved"
                            ? "#1e40af"
                            : "#374151",
                        fontWeight: 500,
                        }}
                    />
                    </TableCell>

                    {/* Total Raised */}
                    <TableCell sx={{ fontWeight: "bold" }}>
                    {fund.totalRaised}
                    </TableCell>

                    {/* Manage */}
                    <TableCell>
                    <Button
                        variant="outlined"
                        startIcon={fund.action === "Start" ? <PlayArrow /> : <Stop />}
                        size="small"
                        onClick={(e) => {
                          setActionAnchor(e.currentTarget);
                          setActionIndex(index);
                        }}
                        sx={{
                        textTransform: "none",
                        borderRadius: 4,
                        borderColor:
                            fund.action === "Start" ? "#10b981" : "#ef4444",
                        color:
                            fund.action === "Start" ? "#10b981" : "#ef4444",
                        bgcolor:
                            fund.action === "Start" ? "#ecfdf5" : "#fef2f2",
                        "&:hover": {
                            borderColor:
                            fund.action === "Start" ? "#059669" : "#dc2626",
                            bgcolor:
                            fund.action === "Start" ? "#d1fae5" : "#fee2e2",
                        },
                        }}
                    >
                        {fund.action}
                    </Button>
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
        </Table>

        <Menu
          anchorEl={statusAnchor}
          open={Boolean(statusAnchor)}
          onClose={() => setStatusAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              if (statusIndex !== null) {
                const next = [...rows];
                next[statusIndex].status = "Approved";
                setRows(next);
              }
              setStatusAnchor(null);
              setStatusIndex(null);
            }}
          >
            Approved
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (statusIndex !== null) {
                const next = [...rows];
                next[statusIndex].status = "Not Approved";
                setRows(next);
              }
              setStatusAnchor(null);
              setStatusIndex(null);
            }}
          >
            Not Approved
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={actionAnchor}
          open={Boolean(actionAnchor)}
          onClose={() => setActionAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              if (actionIndex !== null) {
                const next = [...rows];
                next[actionIndex].action = "Start";
                setRows(next);
              }
              setActionAnchor(null);
              setActionIndex(null);
            }}
          >
            Start
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (actionIndex !== null) {
                const next = [...rows];
                next[actionIndex].action = "Stop";
                setRows(next);
              }
              setActionAnchor(null);
              setActionIndex(null);
            }}
          >
            Stop
          </MenuItem>
        </Menu>
      </Paper>

      {/* Donations Dialog */}
      <Dialog
        open={donationsDialogOpen}
        onClose={() => setDonationsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              All Donations For {selectedFund?.fundName || ""}
            </Typography>
            <IconButton
              onClick={() => setDonationsDialogOpen(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFund?.bookingId && (
            <DonationsTable
              bookingId={selectedFund.bookingId}
              title={`All Donations For ${selectedFund.fundName}`}
              ordering="-created_at"
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Insights;
